import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faSoccerBall, faShield, faBrain, faSeedling, faCrosshairs, faClock, faMoneyBill, faPersonRunning, faAward, faHandshake, faChartBar, faGamepad, faTrophy, faStar, faChartLine, faAmbulance, faUserTie, faDumbbell, faFaceSmile, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { GameState, DisplayFixture, Morale } from '../../../domain/entities';
import { View } from '../../../App';
import { MAX_SQUAD_SIZE, ASSISTANT_MANAGER_LEVELS } from '../../../domain/constants';
import { formatValue } from '../../../utils/utilities';
import { useTeamMetrics } from '../../../hooks/useTeamMetrics';
import { calculateTeamStrengths } from '../../../services/MatchSimulator';
import { soundService } from '../../../services/SoundService';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';
import { CollapsiblePanel } from '../../shared/CollapsiblePanel';
import { FormDisplay } from '../../shared/FormDisplay';
import { TeamStatItem } from './TeamStatItem';
import './TeamStatsPanel.css';

interface TeamOverviewPanelProps {
    gameState: GameState;
    pushView: (view: View) => void;
    last5Fixtures?: DisplayFixture[];
    onFixtureClick?: (fixture: DisplayFixture) => void;
}

// Helper function to generate className strings with conditional compact mode
const getClassNames = (baseClass: string, isCompact: boolean, compactClass?: string): string => {
    const compact = compactClass || `${baseClass}-compact`;
    return isCompact ? `${baseClass} ${compact}` : baseClass;
};

export const TeamOverviewPanel: React.FC<TeamOverviewPanelProps> = ({ gameState, pushView, last5Fixtures = [], onFixtureClick }) => {
    const { isCompact, toggleView } = usePanelViewPreferences();

    const playerTeam = useMemo(() =>
        gameState.leagues[gameState.playerLeagueName]?.teams.find(t => t.name === gameState.playerTeamName),
        [gameState]
    );
    const leagueName = gameState.playerLeagueName;

    const metrics = useTeamMetrics({ team: playerTeam!, gameState, leagueName });

    // Calculate squad averages by position
    const squadAverages = useMemo(() => {
        if (!playerTeam) return { gk: 0, def: 0, mid: 0, fwd: 0 };

        const byPosition = {
            GK: playerTeam.squad.filter(p => p.position === 'GK'),
            DEF: playerTeam.squad.filter(p => p.position === 'DEF'),
            MID: playerTeam.squad.filter(p => p.position === 'MID'),
            FWD: playerTeam.squad.filter(p => p.position === 'FWD'),
        };

        return {
            gk: byPosition.GK.length > 0
                ? byPosition.GK.reduce((sum, p) => sum + p.skill, 0) / byPosition.GK.length
                : 0,
            def: byPosition.DEF.length > 0
                ? byPosition.DEF.reduce((sum, p) => sum + p.skill, 0) / byPosition.DEF.length
                : 0,
            mid: byPosition.MID.length > 0
                ? byPosition.MID.reduce((sum, p) => sum + p.skill, 0) / byPosition.MID.length
                : 0,
            fwd: byPosition.FWD.length > 0
                ? byPosition.FWD.reduce((sum, p) => sum + p.skill, 0) / byPosition.FWD.length
                : 0,
        };
    }, [playerTeam]);

    // Calculate current XI match engine strengths
    const xiStrengths = useMemo(() => {
        if (!playerTeam) return { gk: 0, def: 0, mid: 0, fwd: 0 };
        return calculateTeamStrengths(playerTeam);
    }, [playerTeam]);

    // Calculate season performance stats
    const seasonPerformance = useMemo(() => {
        if (!playerTeam) return { gamesPlayed: 0, avgRating: 0, goalsFor: 0, winLossRatio: '0%' };

        const gamesPlayed = playerTeam.played || 0;
        const goalsFor = playerTeam.goalsFor || 0;

        // Calculate average rating from all players' recent ratings
        let totalRatings = 0;
        let ratingCount = 0;
        playerTeam.squad.forEach(player => {
            if (player.recentRatings && player.recentRatings.length > 0) {
                totalRatings += player.recentRatings.reduce((sum, r) => sum + r, 0);
                ratingCount += player.recentRatings.length;
            }
        });
        const avgRating = ratingCount > 0 ? (totalRatings / ratingCount).toFixed(2) : '--';

        // Calculate win percentage
        const won = playerTeam.won || 0;
        const lost = playerTeam.lost || 0;
        const winLossRatio = gamesPlayed > 0 ? `${Math.round((won / gamesPlayed) * 100)}%` : '0%';

        return { gamesPlayed, avgRating, goalsFor, winLossRatio };
    }, [playerTeam]);

    // Calculate team morale and form
    const teamOverview = useMemo(() => {
        if (!playerTeam) return {
            morale: 'Content' as Morale,
            teamFormResults: [],
            teamFormFixtures: [],
            trainingLevel: 0,
            youthLevel: 0,
        };

        const moraleValues: Record<Morale, number> = { 'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1 };
        const avgMorale = playerTeam.squad.reduce((sum, p) => sum + moraleValues[p.morale], 0) / playerTeam.squad.length;
        let moraleText: Morale = 'Content';
        if (avgMorale > 4.2) moraleText = 'Ecstatic'; else if (avgMorale > 3.5) moraleText = 'Happy'; else if (avgMorale < 2.5) moraleText = 'Unhappy'; else if (avgMorale < 2) moraleText = 'Furious';

        const teamFormResults: ('W' | 'D' | 'L')[] = last5Fixtures.map(f => {
            const { result, home, away } = f;
            if (!result) return 'D';
            if (result.homeScore === result.awayScore) return 'D';
            return ((home === playerTeam.name && result.homeScore > result.awayScore) || (away === playerTeam.name && result.awayScore > result.homeScore)) ? 'W' : 'L';
        });

        return {
            morale: moraleText,
            teamFormResults,
            teamFormFixtures: last5Fixtures,
            trainingLevel: playerTeam.trainingFacilities?.level || 1,
            youthLevel: playerTeam.youthAcademy?.level || 1,
        };
    }, [playerTeam, last5Fixtures]);

    if (!playerTeam) return null;

    const isCompactView = isCompact('teamOverview');

    return (
        <CollapsiblePanel title="Squad Overview" className="panel team-stats-panel" panelId="squad-overview-dashboard">
            <div className="panel-content" style={{ position: 'relative' }}>
                <button
                    onClick={() => toggleView('teamOverview')}
                    style={{
                        position: 'absolute',
                        top: '-32px',
                        right: '0',
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                    title={isCompactView ? 'Show full view' : 'Show compact view'}
                >
                    <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                </button>
                {/* Quick Stats Grid */}
                <div className={getClassNames("team-stats-section", isCompactView)}>
                    <h4 className="team-stats-section-title">Quick Overview</h4>
                    <div className={getClassNames("team-stats-grid", isCompactView)}>
                        <div className="team-stat-item">
                            <div className="team-stat-item-icon"><FontAwesomeIcon icon={faFaceSmile} /></div>
                            <div className="team-stat-item-content">
                                <div className="team-stat-item-value">{teamOverview.morale}</div>
                                <div className="team-stat-item-label">Morale</div>
                            </div>
                        </div>
                        <div className="team-stat-item">
                            <div className="team-stat-item-icon"><FontAwesomeIcon icon={faChartBar} /></div>
                            <div className="team-stat-item-content">
                                <div className="team-stat-item-value" style={{ fontSize: '0.85em', padding: '2px' }}>
                                    <FormDisplay
                                        results={teamOverview.teamFormResults}
                                        fixtures={teamOverview.teamFormFixtures}
                                        onFixtureClick={onFixtureClick}
                                    />
                                </div>
                                <div className="team-stat-item-label">Form (L5)</div>
                            </div>
                        </div>
                        <button className="team-stat-item team-stat-item-clickable" onClick={() => { soundService.playClick(); pushView({ type: 'training', title: 'Training' }); }} title="Go to Training">
                            <div className="team-stat-item-icon"><FontAwesomeIcon icon={faDumbbell} /></div>
                            <div className="team-stat-item-content">
                                <div className="team-stat-item-value">Lv {teamOverview.trainingLevel}</div>
                                <div className="team-stat-item-label">Training</div>
                            </div>
                        </button>
                        <button className="team-stat-item team-stat-item-clickable" onClick={() => { soundService.playClick(); pushView({ type: 'youth', title: 'Youth Academy' }); }} title="Go to Youth Academy">
                            <div className="team-stat-item-icon"><FontAwesomeIcon icon={faSeedling} /></div>
                            <div className="team-stat-item-content">
                                <div className="team-stat-item-value">Lv {teamOverview.youthLevel}</div>
                                <div className="team-stat-item-label">Academy</div>
                            </div>
                        </button>
                    </div>
                </div>

                <div className={getClassNames("team-stats-container", isCompactView)}>
                    {/* Squad Average Strength */}
                    <div className={getClassNames("team-stats-section", isCompactView)}>
                        <h4 className="team-stats-section-title"><FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px' }} />Squad Average (by position)</h4>
                        <div className={getClassNames("team-stats-grid", isCompactView)}>
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="GK"
                                value={Math.round(squadAverages.gk)}
                                fullLabel="Goalkeeper Average"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="DEF"
                                value={Math.round(squadAverages.def)}
                                fullLabel="Defender Average"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="MID"
                                value={Math.round(squadAverages.mid)}
                                fullLabel="Midfielder Average"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="FWD"
                                value={Math.round(squadAverages.fwd)}
                                fullLabel="Forward Average"
                            />
                        </div>
                    </div>

                    {/* Current XI Match Strength */}
                    <div className={getClassNames("team-stats-section", isCompactView)}>
                        <h4 className="team-stats-section-title"><FontAwesomeIcon icon={faSoccerBall} style={{ marginRight: '8px' }} />Current XI (match engine)</h4>
                        <div className={getClassNames("team-stats-grid", isCompactView)}>
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="GK"
                                value={Math.round(xiStrengths.gk)}
                                onClick={() => pushView({ type: 'tactics', title: 'Tactics' })}
                                fullLabel="Goalkeeper Strength"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Def"
                                value={Math.round(xiStrengths.def)}
                                onClick={() => pushView({ type: 'tactics', title: 'Tactics' })}
                                fullLabel="Def Strength"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Mid"
                                value={Math.round(xiStrengths.mid)}
                                onClick={() => pushView({ type: 'tactics', title: 'Tactics' })}
                                fullLabel="Mid Strength"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Fwd"
                                value={Math.round(xiStrengths.fwd)}
                                onClick={() => pushView({ type: 'tactics', title: 'Tactics' })}
                                fullLabel="Fwd Strength"
                            />
                        </div>
                    </div>

                    {/* Season Performance */}
                    <div className={getClassNames("team-stats-section", isCompactView)}>
                        <h4 className="team-stats-section-title"><FontAwesomeIcon icon={faChartLine} style={{ marginRight: '8px' }} />Season Performance</h4>
                        <div className={getClassNames("team-stats-grid", isCompactView)}>
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Played"
                                value={seasonPerformance.gamesPlayed}
                                onClick={() => pushView({ type: 'statistics', title: 'Statistics' })}
                                fullLabel="Games Played This Season"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Avg Rating"
                                value={seasonPerformance.avgRating}
                                fullLabel="Average Team Rating"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Goals"
                                value={seasonPerformance.goalsFor}
                                onClick={() => pushView({ type: 'statistics', title: 'Statistics' })}
                                fullLabel="Total Goals Scored"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Win %"
                                value={seasonPerformance.winLossRatio}
                                onClick={() => pushView({ type: 'statistics', title: 'Statistics' })}
                                fullLabel="Win Percentage"
                            />
                        </div>
                    </div>

                    {/* Team Metrics */}
                    <div className={getClassNames("team-stats-section", isCompactView)}>
                        <h4 className="team-stats-section-title"><FontAwesomeIcon icon={faChartBar} style={{ marginRight: '8px' }} />Team Metrics</h4>
                        <div className={getClassNames("team-stats-grid", isCompactView)}>
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Avg Age"
                                value={metrics.avgAge.toFixed(1)}
                                onClick={() => pushView({ type: 'team', title: 'My Team' })}
                                fullLabel="Average Player Age"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Total Val"
                                value={formatValue(metrics.totalValue)}
                                onClick={() => pushView({ type: 'finances', title: 'Finances' })}
                                fullLabel="Total Squad Value"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Weekly"
                                value={formatValue(metrics.weeklyWage)}
                                onClick={() => pushView({ type: 'finances', title: 'Finances' })}
                                fullLabel="Weekly Wage Bill"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Squad"
                                value={`${playerTeam.squad.length}/${MAX_SQUAD_SIZE}`}
                                onClick={() => pushView({ type: 'team', title: 'My Team', activeTab: 'list' })}
                                fullLabel="Squad Size"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Injured"
                                value={metrics.injuredCount}
                                type={metrics.injuredCount > 0 ? 'error' : 'default'}
                                onClick={() => pushView({ type: 'team', title: 'My Team', activeTab: 'list' })}
                                fullLabel="Injured Players"
                            />
                            <TeamStatItem
                                isCompactView={isCompactView}
                                shortLabel="Asst Mgr"
                                value={playerTeam.assistantManager.level}
                                onClick={() => pushView({ type: 'manager', title: 'Manager', activeTab: 'assistantManager' })}
                                fullLabel={`${ASSISTANT_MANAGER_LEVELS[playerTeam.assistantManager.level - 1].name}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </CollapsiblePanel>
    );
};
